import { API_BASE_URL } from '../config';
import { BaseResponse } from '../types/api';
import { store } from '../store';

// Modified to match backend response structure
interface ApiResponse<T = any> extends BaseResponse<T> {}

class ApiService {
  private baseUrl: string;
  private timeout: number = 10000; // Default timeout of 10 seconds

  constructor(timeout?: number) {
    this.baseUrl = API_BASE_URL;
    if (timeout) {
      this.timeout = timeout;
    }
  }

  private getHeaders(): HeadersInit {
    const token = store.getState().auth.token;
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (response.status === 403 || response.status === 401) {
      // 获取错误信息并传递到登录页面
      try {
        const errorData = await response.json();
        const errorMessage = encodeURIComponent(errorData.message || 'Authentication required');
        window.location.href = `/login?error=${errorMessage}`;
      } catch (e) {
        // 如果无法解析错误响应，则使用默认错误信息
        window.location.href = '/login?error=Authentication required';
      }
      throw new Error('Authentication required. Redirecting to login page.');
    }
    
    if (!response.ok) {
      const error = await response.json();
      console.error('API error response:', error);
      throw new Error(error.message || 'Network response was not ok');
    }
    
    // Parse the response
    const responseJson = await response.json();
    
    // Check if the response follows the expected structure
    if (responseJson && typeof responseJson === 'object' && 'status' in responseJson) {
      const responseData = responseJson as ApiResponse<T>;
      
      // Check if the response status is SUCCESS
      if (responseData.status !== 'SUCCESS') {
        throw new Error(responseData.message || 'Request failed');
      }
      
      // Return only the data part if it exists
      if (responseData.data === undefined) {
        console.warn('Response data is undefined:', responseData);
        return {} as T; // Return empty object as fallback
      }
      
      // console.log('Extracted data from response:', responseData.data);
      return responseData.data as T;
    } else {
      // If the response doesn't follow the expected structure, return it as is
      console.warn('Response does not follow expected structure, returning as is:', responseJson);
      return responseJson as T;
    }
  }

  private async fetchWithTimeout(url: string, options: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error(`Request timeout after ${this.timeout}ms`);
      }
      throw error;
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}${endpoint}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      console.error('GET request failed:', error);
      throw error;
    }
  }

  async post<T>(endpoint: string, data: any): Promise<T> {
    try {
      // console.log(`Making POST request to ${endpoint} with data:`, data);
      const response = await this.fetchWithTimeout(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      console.error('POST request failed:', error);
      throw error;
    }
  }

  async put<T>(endpoint: string, data: any): Promise<T> {
    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      console.error('PUT request failed:', error);
      throw error;
    }
  }

  async delete<T>(endpoint: string): Promise<T> {
    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      console.error('DELETE request failed:', error);
      throw error;
    }
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    try {
      // 使用仅授权头，避免强制 JSON Content-Type
      const token = store.getState().auth.token;
      const headers: HeadersInit = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const response = await this.fetchWithTimeout(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      console.error('UPLOAD request failed:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService();